import { jsx as _jsx } from "react/jsx-runtime";
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { convertToCamelCase } from '@vista-digital/utilities';
import { observer } from 'mobx-react';
import { useCallback, useContext } from 'react';
import { useI18n } from '../../context/occ/hooks/use-i18n';
import { useOccConfig } from '../../context/occ/hooks/use-occ-config';
import { InvalidFieldNameError } from '../../types/invalid-field-name-error';
import { CaptchaField } from '../form/field/captcha/captcha-field';
import { CheckboxField } from '../form/field/checkbox/checkbox-field';
import { CheckboxGroupField } from '../form/field/checkbox-group/checkbox-group-field';
import { DateField } from '../form/field/date/date-field';
import { DropdownField } from '../form/field/dropdown/dropdown-field';
import { InputField } from '../form/field/input/input-field';
import { RadioGroupField } from '../form/field/radio-group/radio-group-field';
import { getFieldClassName, getFieldId } from '../form/field/utils/field-attribute-utils';
import { InputType } from '../form/input/input/types/type';
import { MemberFormContextInstance } from '../member-form/types/context';
import { MemberFormGenderFieldType } from './types/config/gender-field';
import { MemberFormFieldName } from './types/name';
import { isCheckboxFieldProps, isInputFieldProps } from './types/props';
import { getAutoCompleteType } from './utils/get-auto-complete-type';
import { getGenderOptions, getGenreOptions, getPrimarySiteFieldOptions } from './utils/get-options';
const b = bemClassBuilder('v-member-form-field');
export const MemberFormField = observer(props => {
    const i18n = useI18n();
    const { locale: localeConfig } = useOccConfig();
    const { name, readonly, enableValidatingIndicator, validatingIconName, classBuilder } = props;
    const { formData, setFieldValue, validationResult, config, validator, captchaAction } = useContext(MemberFormContextInstance);
    const nameInCamelCase = convertToCamelCase(name);
    const inputValue = formData[nameInCamelCase];
    const fieldValidationConfig = config.validation[nameInCamelCase];
    const handleValueChanged = useCallback((newValue) => {
        if (readonly) {
            return;
        }
        setFieldValue(nameInCamelCase, newValue);
    }, [nameInCamelCase, readonly, setFieldValue]);
    const commonFieldProps = {
        id: getFieldId(classBuilder || b, name),
        name,
        value: inputValue,
        className: getFieldClassName(b, name, classBuilder),
        error: validationResult.validationErrors[nameInCamelCase],
        disabled: readonly,
        validationConfig: fieldValidationConfig,
        enableValidatingIndicator,
        validatingIconName,
        autoCompleteType: getAutoCompleteType(name),
        onChangeValue: handleValueChanged,
        validate: (newValue) => validator.validateField(nameInCamelCase, newValue)
    };
    if (isInputFieldProps(props)) {
        const inputFieldProps = Object.assign(Object.assign({}, commonFieldProps), { displayText: props.displayText });
        switch (props.name) {
            case MemberFormFieldName.MobileNumber:
            case MemberFormFieldName.HomeNumber:
                return _jsx(InputField, Object.assign({}, inputFieldProps, { type: InputType.Phone }));
            default:
                return _jsx(InputField, Object.assign({}, inputFieldProps));
        }
    }
    if (isCheckboxFieldProps(props)) {
        return (_jsx(CheckboxField, Object.assign({}, commonFieldProps, { showLabel: false, value: (props.checkedValue === false ? !inputValue : inputValue), validationConfig: fieldValidationConfig, displayText: props.displayText, onChangeValue: newValue => handleValueChanged(props.checkedValue === false ? !newValue : newValue) })));
    }
    switch (props.name) {
        case MemberFormFieldName.DateOfBirth:
            return (_jsx(DateField, Object.assign({}, commonFieldProps, { dateFormat: props.config.mask || localeConfig.date.formats.dateShort, validationConfig: commonFieldProps.validationConfig, displayText: props.displayText })));
        case MemberFormFieldName.Gender: {
            const genderFieldProps = Object.assign(Object.assign({}, commonFieldProps), { validationConfig: fieldValidationConfig, options: getGenderOptions(props.displayText), displayText: props.displayText });
            if (props.config.type === MemberFormGenderFieldType.Dropdown) {
                return _jsx(DropdownField, Object.assign({}, genderFieldProps, { enableSearch: false }));
            }
            return _jsx(RadioGroupField, Object.assign({}, genderFieldProps));
        }
        case MemberFormFieldName.PrimarySite:
            return (_jsx(DropdownField, Object.assign({}, commonFieldProps, { validationConfig: fieldValidationConfig, options: getPrimarySiteFieldOptions(i18n), enableSearch: props.config.enableSearch, displayText: props.displayText, validate: (newValue) => validator.validateField(nameInCamelCase, newValue) })));
        case MemberFormFieldName.FavouriteGenres:
            return (_jsx(CheckboxGroupField, Object.assign({}, commonFieldProps, { options: getGenreOptions(i18n), validationConfig: fieldValidationConfig, selectedOptions: inputValue, displayText: props.displayText, validate: (newValue) => validator.validateField(nameInCamelCase, newValue) })));
        case MemberFormFieldName.Captcha:
            return (_jsx(CaptchaField, Object.assign({}, commonFieldProps, { validationConfig: fieldValidationConfig, displayText: props.displayText, action: captchaAction })));
        default:
            throw new InvalidFieldNameError('MemberFormField', props.name);
    }
});
MemberFormField.displayName = 'MemberFormField';
//# sourceMappingURL=member-form-field.js.map